import { ICourseClassRelease } from '@dtos/Enrollment'
import {
  addDaysUtc,
  formatDateShort,
  isPastDate,
  isReleaseDayReached,
  isValidDateString,
} from '@utils/dateFormatter'

export interface IContentAvailableProps {
  releaseAt: string | null
  releaseAfter: number | null
  lockAt: string | null
  lockAfter: number | null
  createdAt: string
  availableUntil?: string
  // Liberação por turma: contexto da matrícula + offset do próprio conteúdo.
  courseClassRelease?: ICourseClassRelease | null
  courseClassReleaseOffset?: number | null
}

/**
 * Data de liberação por turma, ou null quando o modo turma não se aplica.
 *
 * Espelha BlockDueTime#course_class_release_at_for (services/core). Qualquer
 * divergência aqui faz a data exibida ao aluno diferir da que o servidor
 * aplica, então as três guardas abaixo precisam continuar iguais às do Ruby:
 *
 * 1. toggle desligado no curso            -> null (legado)
 * 2. matrícula sem turma vinculada        -> null (legado, fail-open)
 * 3. sem offset na aula nem no curso      -> null (legado)
 *
 * O offset da aula tem precedência sobre o padrão do curso. Usar `??` e nunca
 * `||`: offset 0 significa "libera no dia de início da turma" e é diferente de
 * nulo ("usa o padrão do curso"). Em Ruby `0 ||= x` mantém 0; em JS
 * `0 || x` devolveria x e as datas divergiriam.
 *
 * A soma usa addDaysUtc (e não addDays) para preservar o instante real: addDays
 * desloca pelo fuso da máquina antes de serializar, e o resultado ainda é lido
 * por isReleaseDayReached/formatDateShort — o deslocamento virava o dia da data
 * exibida quando o horário original caía no fim do dia UTC.
 */
export function courseClassReleaseDate({
  courseClassRelease,
  courseClassReleaseOffset,
}: Pick<
  IContentAvailableProps,
  'courseClassRelease' | 'courseClassReleaseOffset'
>): string | null {
  if (!courseClassRelease?.enabled) return null
  // Data inválida derrubaria o render em toISOString (RangeError), então cai no
  // legado em vez de quebrar a página.
  if (!isValidDateString(courseClassRelease.startAt)) return null

  const offset = courseClassReleaseOffset ?? courseClassRelease.defaultOffset
  if (offset === null || offset === undefined) return null

  return addDaysUtc(courseClassRelease.startAt as string, offset)
}

/**
 * Data em que o conteúdo é liberado, ou null quando não há restrição de
 * liberação. Espelha BlockDueTime#release_at_for: turma primeiro, legado depois.
 *
 * Diferente de releaseContentDateText, devolve a data crua — serve para
 * comparar conteúdos entre si (ex.: qual libera primeiro).
 */
export function contentReleaseDate(
  props: IContentAvailableProps,
): string | null {
  const { releaseAt, releaseAfter, createdAt } = props

  const cohortReleaseAt = courseClassReleaseDate(props)
  if (cohortReleaseAt) return cohortReleaseAt

  if (releaseAt) return releaseAt
  if (releaseAfter && createdAt) return addDaysUtc(createdAt, releaseAfter)

  return null
}

export function isContentAvailable(props: IContentAvailableProps) {
  const {
    releaseAfter,
    lockAfter,
    releaseAt,
    lockAt,
    createdAt,
    availableUntil,
  } = props

  if (!createdAt) return true

  const cohortReleaseAt = courseClassReleaseDate(props)

  // Quando o modo turma resolve uma data, ela substitui releaseAt/releaseAfter
  // por completo — mesma precedência do `||` em BlockDueTime#release_at_for.
  // lockAt/lockAfter/availableUntil não são afetados por turma.
  //
  // Liberação é por dia (isReleaseDayReached, espelhando o beginning_of_day do
  // servidor); bloqueio continua no instante, que é como lock_at é aplicado lá.
  const isReleased = cohortReleaseAt
    ? isReleaseDayReached(cohortReleaseAt)
    : (releaseAt ? isReleaseDayReached(releaseAt) : true) &&
      (releaseAfter
        ? isReleaseDayReached(addDaysUtc(createdAt, releaseAfter))
        : true)

  const isLockedAt = lockAt ? !isPastDate(lockAt) : true
  const isLockedAfter = lockAfter
    ? !isPastDate(addDaysUtc(createdAt, lockAfter))
    : true
  const isStillAvailable = availableUntil ? !isPastDate(availableUntil) : true

  return isReleased && isLockedAt && isLockedAfter && isStillAvailable
}

export function releaseContentDateText(props: IContentAvailableProps) {
  const { releaseAt, releaseAfter, lockAt, lockAfter, createdAt } = props

  if (!createdAt) return ''

  const hasReleaseAt = Boolean(releaseAt)
  // releaseAfter 0 = "libera na inscrição", ou seja, sem restrição — é como
  // isContentAvailable e contentReleaseDate já tratam. Antes daqui saía um
  // "Libera em <data da matrícula>" em conteúdo que estava disponível.
  const hasReleaseAfter = Boolean(releaseAfter)
  const hasLockAt = Boolean(lockAt)
  const hasLockAfter = lockAfter !== null && lockAfter !== undefined

  const releaseAfterDate = hasReleaseAfter
    ? addDaysUtc(createdAt, releaseAfter as number)
    : null
  const lockAfterDate = hasLockAfter
    ? addDaysUtc(createdAt, lockAfter as number)
    : null

  const getReleaseAtText = () =>
    releaseAt ? `Libera em ${formatDateShort(releaseAt)}` : ''

  const getReleaseAfterText = () =>
    releaseAfterDate ? `Libera em ${formatDateShort(releaseAfterDate)}` : ''

  // lockAt/lockAfter marcam quando o conteúdo é BLOQUEADO. Se a data ainda está
  // no futuro, o conteúdo continua disponível até lá ("Disponível até"); se já
  // passou, está bloqueado ("Bloqueado em").
  const getLockAtText = () => {
    if (!lockAt) return ''
    if (isPastDate(lockAt)) {
      return `Bloqueado em ${formatDateShort(lockAt)}`
    }
    return `Disponível até ${formatDateShort(lockAt)}`
  }

  const getLockAfterText = () => {
    if (!lockAfterDate) return ''
    if (isPastDate(lockAfterDate)) {
      return `Bloqueado em ${formatDateShort(lockAfterDate)}`
    }

    return `Disponível até ${formatDateShort(lockAfterDate)}`
  }

  // Modo turma vence releaseAt/releaseAfter. Enquanto a data não chega mostra
  // "Libera em"; depois de liberado segue para os textos de bloqueio, como nos
  // ramos legados abaixo.
  const cohortReleaseAt = courseClassReleaseDate(props)
  if (cohortReleaseAt) {
    if (!isReleaseDayReached(cohortReleaseAt)) {
      return `Libera em ${formatDateShort(cohortReleaseAt)}`
    }
    if (hasLockAt) return getLockAtText()
    if (hasLockAfter) return getLockAfterText()
    return ''
  }

  // "Libera em" só vale enquanto o dia da liberação não chegou — mesma regra do
  // ramo de turma acima. Depois de liberado o conteúdo cai nos textos de
  // bloqueio, ou fica sem texto quando não há lockAt/lockAfter. Antes daqui o
  // conteúdo já liberado sem bloqueio continuava anunciando "Libera em <data
  // passada>", que era lido pelo aluno como aula ainda indisponível.
  if (hasReleaseAt && !isReleaseDayReached(releaseAt as string)) {
    return getReleaseAtText()
  }

  if (
    hasReleaseAfter &&
    releaseAfterDate &&
    !isReleaseDayReached(releaseAfterDate)
  ) {
    return getReleaseAfterText()
  }

  if (hasLockAt) return getLockAtText()
  if (hasLockAfter) return getLockAfterText()

  return ''
}
